import { Form, message, Modal, Select, Skeleton } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/Button/Button';
import { apiInstance } from '../../../utils/api';

const FREE_STATUS_ID = 'df17a6d7-2510-4a1e-9692-f9c34d66b156';
const ASSIGNED_STATUS_ID = '3075a30b-f6e8-45cd-990b-28399db75400';
const ISSUED_STATUS_ID = '2ec4e1b9-b7f5-4eb2-9a83-695c9b1be716';

const ContactCard = ({
  contact = {},
  isNewContact = false,
  cards = [],
  references,
  onReloadCards,
  onContactReload,
  isLoading = false,
}) => {
  const [isAssignLoading, setIsAssignLoading] = useState(false);
  const [isWithdrawLoading, setIsWithdrawLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState();
  const [cardAnimation, setCardAnimation] = useState(''); // 'withdraw' or 'delete' or ''
  const [isBlockModalVisible, setIsBlockModalVisible] = useState(false);
  const [blockReasons, setBlockReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState(undefined);
  const [isBlockReasonsLoading, setIsBlockReasonsLoading] = useState(false);
  const [isWithdrawModalVisible, setIsWithdrawModalVisible] = useState(false);
  const [withdrawReasons, setWithdrawReasons] = useState([]);
  const [selectedWithdrawReasonId, setSelectedWithdrawReasonId] = useState(undefined);
  const [isWithdrawReasonsLoading, setIsWithdrawReasonsLoading] = useState(false);
 
  const hasAssignedCard =
    Boolean(contact?.RespCardId) &&
    (contact?.RespCardStatus?.value === ASSIGNED_STATUS_ID ||
      contact?.RespCardStatus?.value === ISSUED_STATUS_ID);
  const isCardOrdered = Boolean(contact?.isCardOrdered);

  const availableCards = useMemo(
    () => cards.filter((card) => card.RespSCAStatus?.value === FREE_STATUS_ID),
    [cards]
  );
  const hasAvailableCards = availableCards.length > 0;

  const cleanCardLabel = useCallback((value) => {
    if (value === null || value === undefined) return '';

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        for (const item of value) {
          const candidate = cleanCardLabel(item);
          if (candidate) {
            return candidate;
          }
        }
        return '';
      }

      const nestedValue =
        value.label ??
        value.text ??
        value.displayValue ??
        value.value ??
        value.Name ??
        value.name ??
        value.displayName ??
        value.CardNumber ??
        value.cardNumber ??
        value.Number ??
        value.number ??
        value.Id ??
        value.id ??
        value.title ??
        value.caption ??
        value.description ??
        value.content;

      if (nestedValue !== undefined) {
        return cleanCardLabel(nestedValue);
      }

      return '';
    }

    if (typeof value === 'number') {
      return String(value);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';

      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) {
        return '';
      }

      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        return '';
      }

      return trimmed;
    }

    return '';
  }, []);

  const getCardLabel = useCallback(
    (card) => {
      if (!card) return '';

      const candidates = [
        card.Name,
        card.CardNumber,
        card.cardNumber,
        card.Number,
        card.number,
        card.RespCardNumber,
        card.displayName,
        card.title,
        card.Id,
      ];

      for (const candidate of candidates) {
        const value = cleanCardLabel(candidate);
        if (value) {
          return value;
        }
      }

      return '';
    },
    [cleanCardLabel]
  );

  const shouldBlink = !isNewContact && !hasAssignedCard && !isCardOrdered && hasAvailableCards;

  useEffect(() => {
    if (hasAssignedCard || isCardOrdered) {
      setSelectedCardId(undefined);
    }
  }, [hasAssignedCard, isCardOrdered]);

  const refreshData = async () => {
    await Promise.all([onContactReload?.(), onReloadCards?.()]);
  };

  const assignCard = async () => {
    if (!selectedCardId) {
      message.warning('Будь ласка, оберіть картку зі списку');
      return;
    }

    if (!contact?.Id) return;

    setIsAssignLoading(true);
    try {
      await apiInstance(`${process.env.REACT_APP_API_URL}/api/post-data?type=assign-card`, 'POST', {
        cardId: selectedCardId,
        contactId: contact.Id,
      });
      message.success('Картку успішно призначено');
      await refreshData();
      setSelectedCardId(undefined);
    } catch (error) {
      console.error('Помилка призначення картки:', error);
      message.error(error?.message || 'Помилка призначення картки');
    } finally {
      setIsAssignLoading(false);
    }
  };

  const withdrawCard = async () => {
    if (!contact?.RespCardId) return;
    setIsWithdrawLoading(true);
    setCardAnimation('withdraw');

    // Даем время для быстрой smooth анимации (800ms)
    await new Promise((resolve) => setTimeout(resolve, 800));

    try {
      await apiInstance(
        `${process.env.REACT_APP_API_URL}/api/post-data?type=withdraw-card`,
        'POST',
        {
          cardId: contact.RespCardId,
          withdrawReasonId: selectedWithdrawReasonId,
        }
      );
      message.success('Картку успішно вилучено');
      await refreshData();
      setCardAnimation('');
    } catch (error) {
      console.error('Помилка вилучення картки:', error);
      message.error(error?.message || 'Помилка вилучення картки');
      setCardAnimation('');
    } finally {
      setIsWithdrawLoading(false);
    }
  };

  const fetchWithdrawReasons = async () => {
    setIsWithdrawReasonsLoading(true);
    try {
      const response = await apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=card-withdraw-reasons`,
        'GET'
      );
      if (response && response.rows) {
        setWithdrawReasons(response.rows);
      }
    } catch (error) {
      console.error('Помилка завантаження причин вилучення:', error);
      const fallback = [
        { value: "9bbb3959-6243-46a0-bf22-48c1e008f3e0", displayValue: "Звільнення співробітника" },
        { value: "901cfb6c-c85c-4b4c-94a8-1955bfba81c0", displayValue: "Зміна посади / Переведення" }
      ];
      setWithdrawReasons(fallback);
    } finally {
      setIsWithdrawReasonsLoading(false);
    }
  };

  const handleWithdrawClick = async () => {
    setSelectedWithdrawReasonId(undefined);
    setIsWithdrawModalVisible(true);
    await fetchWithdrawReasons();
  };

  const handleWithdrawConfirm = async () => {
    setIsWithdrawModalVisible(false);
    await withdrawCard();
  };

  const deleteCard = async () => {
    if (!contact?.RespCardId) return;
    setIsDeleteLoading(true);
    setCardAnimation('delete');

    // Даем время для анимации
    await new Promise((resolve) => setTimeout(resolve, 800));

    try {
      await apiInstance(`${process.env.REACT_APP_API_URL}/api/post-data?type=delete-card`, 'POST', {
        cardId: contact.RespCardId,
        blockReasonId: selectedReasonId,
      });
      message.success('Картку заблоковано успішно');
      await refreshData();
      setCardAnimation('');
    } catch (error) {
      console.error('Помилка блокування картки:', error);
      message.error(error?.message || 'Помилка блокування картки');
      setCardAnimation('');
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const fetchBlockReasons = async () => {
    setIsBlockReasonsLoading(true);
    try {
      const response = await apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=card-block-reasons`,
        'GET'
      );
      if (response && response.rows) {
        setBlockReasons(response.rows);
      }
    } catch (error) {
      console.error('Помилка завантаження причин блокування:', error);
      const fallback = [
        { value: "d72d3d60-b37d-42b3-88e8-fbca78fb82dd", displayValue: "Втрата картки" },
        { value: "0bf40dc0-e76f-4072-be16-aa2d7dc1fbdc", displayValue: "Звільнення співробітника" },
        { value: "9bc66c7a-c3fe-4674-8136-21db5483247c", displayValue: "Механічне пошкодження" }
      ];
      setBlockReasons(fallback);
    } finally {
      setIsBlockReasonsLoading(false);
    }
  };

  const handleBlockClick = async () => {
    setSelectedReasonId(undefined);
    setIsBlockModalVisible(true);
    await fetchBlockReasons();
  };

  const handleBlockConfirm = async () => {
    setIsBlockModalVisible(false);
    await deleteCard();
  };



  const canAssignCard = !hasAssignedCard && !isCardOrdered && hasAvailableCards;
  const isAnyButtonLoading = isAssignLoading || isWithdrawLoading || isDeleteLoading;

  // Кнопка "Призначити картку" — тільки якщо є вільні картки
  const actionButton = canAssignCard
    ? {
        text: 'Призначити картку',
        bg: 'blue',
        disabled: !selectedCardId || isAnyButtonLoading,
        onClick: () => assignCard(),
      }
    : null;

  const assignedCardLabel = useMemo(() => {
    if (!contact?.RespCardId && contact?.RespCardNumber) {
      return cleanCardLabel(contact.RespCardNumber);
    }
    const matchedCard = cards.find((card) => card.Id === contact?.RespCardId);
    const label = getCardLabel(matchedCard) || cleanCardLabel(contact?.RespCardNumber);
    return label || '';
  }, [cards, contact?.RespCardId, contact?.RespCardNumber, cleanCardLabel, getCardLabel]);

  if (!contact && !isLoading) return null;

  return (
    <div className="contact-card-root">
      <div className="card-wrapper">
        <div className="card-wrapper__inner">
          <div className="box card-preview-box">
            {isLoading ? (
              <>
                <Skeleton.Input active className="card-skeleton-input" />

                <div className="card-content-center">
                  <div className="card-form-item">
                    <div className="card-select-wrapper">
                      <Skeleton.Input active className="card-skeleton-select" />
                    </div>
                  </div>
                </div>

                <div className="card-preview__actions">
                  <Skeleton.Button active className="card-skeleton-btn" />
                  <Skeleton.Button active className="card-skeleton-btn" />
                  <Skeleton.Button active className="card-skeleton-btn" />
                </div>
              </>
            ) : (
              <>
                <span className="employee-box-header__title">
                  {isNewContact ? 'Створення співробітника' : 'Номер картки:'}
                </span>

                {!isNewContact && hasAssignedCard && (
                  <div
                    className={`card-content-center ${
                      cardAnimation === 'withdraw'
                        ? 'card-animation-withdraw'
                        : cardAnimation === 'delete'
                        ? 'card-animation-delete'
                        : ''
                    }`}
                  >
                    {/* Частинки для анімації видалення */}
                    {cardAnimation === 'delete' && (
                      <>
                        {[...Array(12)].map((_, i) => (
                          <div
                            key={i}
                            className="delete-particle"
                            style={{
                              '--angle': `${i * 30}deg`,
                              '--delay': `${i * 0.05}s`,
                              '--distance': `${80 + Math.random() * 40}px`,
                            }}
                          />
                        ))}
                      </>
                    )}

                    <Form.Item name="cardNumber" className="card-form-item">
                      <div className="card-select-wrapper">
                        <Select
                          className="card-select"
                          value={assignedCardLabel}
                          disabled={true}
                          placeholder="Номер картки"
                        />
                      </div>
                    </Form.Item>
                  </div>
                )}

                {!hasAssignedCard && isCardOrdered && (
                  <div className="card-content-center">
                    <Form.Item name="cardNumber" className="card-form-item">
                      <div className="card-select-wrapper">
                        <Select
                          className="card-select"
                          value="Очікуйте"
                          disabled={true}
                          placeholder="Очікується замовлення"
                        />
                      </div>
                    </Form.Item>
                  </div>
                )}

                {!hasAssignedCard && !isCardOrdered && (
                  <div className="card-content-center">
                    <Form.Item name="cardNumber" className="card-form-item" required>
                      <div
                        className={
                          shouldBlink
                            ? 'card-select-wrapper card-select-wrapper--active'
                            : 'card-select-wrapper'
                        }
                      >
                        <Select
                          className="card-select"
                          placeholder={`Оберіть картку${hasAvailableCards ? '' : ''}`}
                          value={selectedCardId}
                          onChange={setSelectedCardId}
                          options={availableCards.map((card) => ({
                            value: card.Id,
                            label: getCardLabel(card),
                          }))}
                          disabled={isAnyButtonLoading || !availableCards.length}
                          optionFilterProp="label"
                        />
                        {shouldBlink && <div className="card-select-glow" />}
                      </div>
                    </Form.Item>
                  </div>
                )}

                <div className="card-preview__actions">
                  {actionButton && (
                    <Button
                      text={actionButton.text}
                      bg={actionButton.bg}
                      block
                      loading={isAssignLoading}
                      disabled={actionButton.disabled}
                      onClick={actionButton.onClick}
                      className="contact-assign-btn"
                    />
                  )}
                  <Button
                    text="Вилучити"
                    bg="green"
                    block
                    loading={isWithdrawLoading}
                    disabled={!hasAssignedCard || contact?.RespCardStatus?.value === ISSUED_STATUS_ID || isAnyButtonLoading}
                    onClick={handleWithdrawClick}
                    className={
                      isWithdrawLoading
                        ? 'button--withdraw-active contact-withdraw-btn'
                        : 'contact-withdraw-btn'
                    }
                  />
                  <Button
                    text="Заблокувати "
                    bg="orange"
                    block
                    loading={isDeleteLoading}
                    disabled={!hasAssignedCard || contact?.RespCardStatus?.value === ISSUED_STATUS_ID || isAnyButtonLoading}
                    onClick={handleBlockClick}
                    className={
                      isDeleteLoading
                        ? 'button--delete-active contact-delete-btn'
                        : 'contact-delete-btn'
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>


      <Modal
        className="success-modal"
        visible={isBlockModalVisible}
        onCancel={() => setIsBlockModalVisible(false)}
        footer={
          <div className="ant-btn-wrapper">
            <Button
              onClick={() => setIsBlockModalVisible(false)}
              text="Відмінити"
              bg="orange"
              disabled={isDeleteLoading}
            />
            <Button
              text="Заблокувати"
              bg="turquoise"
              onClick={handleBlockConfirm}
              loading={isDeleteLoading}
              disabled={isBlockReasonsLoading || isDeleteLoading || !selectedReasonId}
            />
          </div>
        }
      >
        <div className="success-modal__wrapperSCA">
          <div className="text-black-medium-f22-l30">Блокування картки СКД</div>
          <div className="text-grey-dark-normal-f16-l28" style={{ marginBottom: '16px' }}>
            Будь ласка, оберіть причину блокування картки:
          </div>
          <Select
            style={{ width: '100%' }}
            loading={isBlockReasonsLoading}
            value={selectedReasonId}
            onChange={setSelectedReasonId}
            options={blockReasons.map((reason) => ({
              value: reason.value,
              label: reason.displayValue,
            }))}
            placeholder="Оберіть причину"
          />
        </div>
      </Modal>

      <Modal
        className="success-modal"
        visible={isWithdrawModalVisible}
        onCancel={() => setIsWithdrawModalVisible(false)}
        footer={
          <div className="ant-btn-wrapper">
            <Button
              onClick={() => setIsWithdrawModalVisible(false)}
              text="Відмінити"
              bg="orange"
              disabled={isWithdrawLoading}
            />
            <Button
              text="Вилучити"
              bg="turquoise"
              onClick={handleWithdrawConfirm}
              loading={isWithdrawLoading}
              disabled={isWithdrawReasonsLoading || isWithdrawLoading || !selectedWithdrawReasonId}
            />
          </div>
        }
      >
        <div className="success-modal__wrapperSCA">
          <div className="text-black-medium-f22-l30">Вилучення картки СКД</div>
          <div className="text-grey-dark-normal-f16-l28" style={{ marginBottom: '16px' }}>
            Будь ласка, оберіть причину вилучення картки:
          </div>
          <Select
            style={{ width: '100%' }}
            loading={isWithdrawReasonsLoading}
            value={selectedWithdrawReasonId}
            onChange={setSelectedWithdrawReasonId}
            options={withdrawReasons.map((reason) => ({
              value: reason.value,
              label: reason.displayValue,
            }))}
            placeholder="Оберіть причину"
          />
        </div>
      </Modal>
    </div>
  );
};

export default ContactCard;
